import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar,
  Check,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  formatDate,
  formatDistanceToNow,
} from "./taskFormatters";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import type { TaskRead, TaskStatusConfig } from "@/api/types";
import { PRIORITY_DOT, PRIORITY_LABEL } from "./priority";
import { paletteFor, type StatusPalette } from "./statusPalette";
import { rowAnim, rowTransition } from "@/components/ui/motion";
import { AttachmentsCell } from "./AttachmentsCell";

interface TaskItemProps {
  task: TaskRead;
  projectSlug: string;
  /** When provided, renders the company link. */
  showCompany?: boolean;
  /** When provided, renders the assignee avatar + name. */
  showAssignee?: boolean;
  /** Optional badge rendered in the meta row — used by the
   *  cross-project "My Tasks" feed to label each row with its
   *  project. */
  projectBadge?: ReactNode;
  canEdit: boolean;
  canDelete: boolean;
  /** Fired when the user clicks the round checkbox. */
  onToggleComplete: (task: TaskRead) => Promise<void> | void;
  onEdit?: (task: TaskRead) => void;
  onDelete?: (task: TaskRead) => void;
  /**
   * Click on the row body (but not the checkbox / company link / dropdown)
   * opens a side panel with the full task detail.
   */
  onOpenDetail?: (task: TaskRead) => void;
  /** Local UI state — true when an optimistic completion just happened. */
  fadingOut?: boolean;
  /** Stagger key for cascade animations from a parent AnimatePresence. */
  index?: number;
  /** Resolved catalog entry for `task.status` — drives the status
   *  badge color in the row. Null when the project hasn't configured
   *  a catalog or the task is on a stale key. */
  statusConfig?: TaskStatusConfig | null;
  /** Palette of the project's "done" status. Drives the round
   *  checkbox color so the visual feedback follows whatever color the
   *  operator picked in Settings. */
  donePalette?: StatusPalette;
}

/** Default checkbox swatch used when the project hasn't configured a
 *  status catalog — emerald keeps the previous look on legacy data. */
const DEFAULT_DONE_PALETTE: StatusPalette = paletteFor("emerald");

export function TaskItem({
  task,
  projectSlug,
  showCompany = true,
  showAssignee = true,
  projectBadge,
  canEdit,
  canDelete,
  onToggleComplete,
  onEdit,
  onDelete,
  onOpenDetail,
  fadingOut,
  index,
  statusConfig,
  donePalette = DEFAULT_DONE_PALETTE,
}: TaskItemProps) {
  const [busy, setBusy] = useState(false);

  async function handleToggle() {
    if (!canEdit || busy) return;
    setBusy(true);
    try {
      await onToggleComplete(task);
    } finally {
      setBusy(false);
    }
  }

  const completed = task.is_completed;
  const rowInteractive = !!onOpenDetail;
  // Cross-project rows (the My Tasks feed) carry their own project
  // slug — link the company to *that* project, not the page's.
  const linkSlug =
    (task as { project_slug?: string }).project_slug ?? projectSlug;
  const delay = index != null ? Math.min(index, 10) * 0.05 : 0;

  return (
    <motion.div
      layout
      initial={rowAnim.initial}
      animate={{
        opacity: fadingOut ? 0.5 : 1,
        y: 0,
      }}
      exit={{ opacity: 0, y: -8, height: 0, marginTop: 0, paddingTop: 0, paddingBottom: 0 }}
      transition={{ ...rowTransition, delay }}
      className={cn(
        "group relative flex items-center gap-3 overflow-hidden px-4 py-3",
        "border-b border-slate-900/[0.06] transition-colors duration-150 last:border-b-0",
        fadingOut ? "pointer-events-none" : "hover:bg-indigo-500/[0.04]",
      )}
      style={{ minHeight: 64, willChange: "transform, opacity" }}
    >
      {/* Checkbox — coloured with the project's first is_done status
       *  palette. Tailwind class swap (with `transition-colors`) handles
       *  the smooth fill, replacing the previously hardcoded emerald
       *  framer-motion animation. */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={!canEdit || busy}
        aria-label={completed ? "Reopen task" : "Complete task"}
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200",
          completed
            ? cn(
                "text-white",
                donePalette.fillBgClass,
                donePalette.fillBorderClass,
              )
            : cn(
                "border-slate-300 bg-white text-transparent",
                donePalette.hoverBorderClass,
                donePalette.hoverSoftBgClass,
                donePalette.hoverTextClass,
              ),
          !canEdit && "cursor-default opacity-60",
        )}
      >
        {busy ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            {completed ? (
              <motion.span
                key="check"
                initial={{ scale: 0, rotate: -45 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 45 }}
                transition={{ type: "spring", stiffness: 600, damping: 18 }}
                className="flex"
              >
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </motion.span>
            ) : (
              <motion.span
                key="empty"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ duration: 0.1 }}
                className="flex"
              >
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </motion.span>
            )}
          </AnimatePresence>
        )}
      </button>

      {/* Title + meta + due + priority — the whole middle area is the click target */}
      <button
        type="button"
        disabled={!rowInteractive}
        onClick={() => onOpenDetail?.(task)}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-3 rounded-md text-left",
          rowInteractive
            ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300/50"
            : "cursor-default",
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="relative inline-block max-w-full">
            <motion.div
              animate={{ color: completed ? "#94a3b8" : "#0f172a" }}
              transition={{ duration: 0.2 }}
              className="truncate text-sm font-semibold"
            >
              {task.title}
            </motion.div>
            <motion.span
              aria-hidden
              initial={false}
              animate={{ scaleX: completed ? 1 : 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              style={{ transformOrigin: "left center" }}
              className="pointer-events-none absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-slate-400"
            />
          </div>
          {projectBadge ||
          (showCompany && task.company_id && task.company_name) ||
          (showAssignee && task.assigned_to_name) ? (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              {projectBadge}
              {showCompany && task.company_id && task.company_name && (
                <Link
                  to={`/projects/${linkSlug}/companies/${task.company_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-500"
                >
                  {task.company_name}
                </Link>
              )}
              {showAssignee && task.assigned_to_name && (
                <span className="inline-flex items-center gap-1.5">
                  <Avatar className="h-5 w-5 border border-slate-200">
                    <AvatarFallback className="bg-slate-900 text-[9px] font-semibold text-white">
                      {initials(task.assigned_to_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{task.assigned_to_name}</span>
                </span>
              )}
            </div>
          ) : null}
        </div>

        {/* Status badge — coloured from the catalog config so it
         *  follows whatever palette the operator picked in Settings.
         *  Skipped when the project hasn't configured statuses or the
         *  task carries a stale key. */}
        {statusConfig && (
          <StatusBadge
            label={statusConfig.label}
            palette={paletteFor(statusConfig.color)}
          />
        )}

        {/* Due date */}
        <DueBadge task={task} />

        {/* Priority dot */}
        <span
          className={cn(
            "h-2.5 w-2.5 shrink-0 rounded-full",
            PRIORITY_DOT[task.priority],
          )}
          title={PRIORITY_LABEL[task.priority]}
          aria-label={`Priority: ${PRIORITY_LABEL[task.priority]}`}
        />
      </button>

      {/* Attachments badge — sits OUTSIDE the middle button so its
       *  click handler (popover open) doesn't bubble up to the
       *  row's open-detail action. Empty cell when the task has
       *  zero attachments or the count is undefined. */}
      <AttachmentsCell
        task={task}
        projectSlug={projectSlug}
        onOpenTaskDetail={onOpenDetail}
      />

      {/* Actions */}
      {(canEdit || canDelete) && (onEdit || onDelete) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded-md p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 group-hover:opacity-100"
              aria-label={`More actions for ${task.title}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {canEdit && onEdit && (
              <DropdownMenuItem onSelect={() => onEdit(task)}>
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Edit
              </DropdownMenuItem>
            )}
            {canEdit && canDelete && onDelete && <DropdownMenuSeparator />}
            {canDelete && onDelete && (
              <DropdownMenuItem
                onSelect={() => onDelete(task)}
                className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </motion.div>
  );
}

function StatusBadge({
  label,
  palette,
}: {
  label: string;
  palette: StatusPalette;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
        palette.softBgClass,
        palette.softTextClass,
      )}
    >
      <span
        className={cn("h-1.5 w-1.5 rounded-full", palette.dotClass)}
        aria-hidden
      />
      {label}
    </span>
  );
}

function DueBadge({ task }: { task: TaskRead }) {
  if (!task.due_at) return null;
  const due = new Date(task.due_at);
  const now = new Date();
  const isToday =
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate();
  const time = due.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  if (task.is_overdue) {
    return (
      <Badge tone="rose">
        <Calendar className="mr-1 h-3 w-3" />
        Overdue · {formatDistanceToNow(due)}
      </Badge>
    );
  }
  if (isToday) {
    return (
      <Badge tone="amber">
        <Calendar className="mr-1 h-3 w-3" />
        Due today · {time}
      </Badge>
    );
  }
  return (
    <Badge tone="slate">
      <Calendar className="mr-1 h-3 w-3" />
      Due {formatDate(due)}
    </Badge>
  );
}

function Badge({
  tone,
  children,
}: {
  tone: "rose" | "amber" | "slate";
  children: React.ReactNode;
}) {
  const tones: Record<string, string> = {
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    slate: "border-slate-200 bg-slate-50 text-slate-600",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}
